/*
  Variant Picker V2 section JS.

  Responsibilities:
    1. Sync the visible Subscribe / One-Time radios into this picker's own
       hidden #nx-sub-toggle-input-v2 compat checkbox, which carries
       [data-nx-sub-toggle] so global.js's resolveSubToggle() and the
       product-form.js fallback path keep working unchanged.
    2. Update the per-panel price display (and the Delivery interval
       label) when the customer picks a different variant pill.
    3. Add/remove .vp-v2__mode--active on the selected mode label so
       the perks list and delivery row appear/hide.
    4. Keep the loyalty-points perk in step with the selected variant and
       quantity, and reconcile the earn rate against Rivo's own config.
    5. Price every figure at the shopper's Intelligems test price when they
       are in a price test (TASKS #133), read through
       assets/intelligems-bridge.js. Outside a test the Liquid-rendered money
       strings are used exactly as before.

  Variant change itself flows through Dawn's <variant-selects> exactly
  like the standard picker — our pill radios sit inside a <fieldset>
  and carry data-option-value-id, so the existing pubsub
  (optionValueSelectionChange) is published and product-info.js
  refetches the main section. No custom variant-change wiring here.
*/

(function () {
  function formatPlanName(name) {
    var n = String(name == null ? "" : name).trim();
    if (!n) return "";
    return /^\d+$/.test(n) ? n + " Days" : n;
  }

  // Intelligems price test (TASKS #133). Null when the Intelligems script isn't
  // installed; every caller then falls back to the Liquid prices.
  function igApi() {
    return window.JiyuIntelligems || null;
  }

  // Attributes Intelligems reads on a price element it rewrites. It re-applies its
  // own price whenever one is inserted, so the multiplier must produce the number
  // this file writes: x 1 for the one-time price, x (1 - plan %) for the
  // subscription price, and either of those over the per-day divisor.
  function igAttrs(productId, variantId, multiplier) {
    if (!productId || !variantId) return "";
    var attrs = ' data-product-id="' + productId + '" data-variant-id="' + variantId + '"';
    if (multiplier != null && isFinite(multiplier) && multiplier !== 1) {
      attrs += ' data-price-multiplier="' + multiplier + '"';
    }
    return attrs;
  }

  // Text read only by screen readers. The words come from locale files through a
  // data attribute, so they are escaped before going into innerHTML.
  function srOnly(text) {
    if (!text) return "";
    var escaped = String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return '<span class="visually-hidden visually-hidden--inline">' + escaped + "</span>";
  }

  // Prices are pre-formatted by Liquid's `money` filter (market-aware) and
  // passed in via the variant JSON as *_money strings, so the JS just inserts
  // them. (window.Shopify.formatMoney isn't defined in this theme, which is why
  // the prices previously rendered empty.) For a shopper in a price test the
  // strings come from intelligems-bridge.js instead. `hooks` holds the
  // Intelligems attribute string for each figure; an empty one leaves that
  // figure untagged. `hooks.compareClass` is igPrice when the struck figure is
  // the one-time price rather than a compare-at (see subCompare). `labels`
  // holds the screen-reader words for a struck pair; they sit beside the price
  // elements, since Intelligems rewrites the text inside them. Same markup as
  // sr_regular_price / sr_sale_price in product-variant-picker-v2.liquid.
  function buildPriceHTML(compareMoney, currentMoney, perDayMoney, perDayAttr, perDayLabel, hooks, labels) {
    if (!currentMoney) return "";
    hooks = hooks || {};
    labels = labels || {};
    var parts = [];
    if (compareMoney) {
      var compareClass = hooks.compareClass || "igComparePrice";
      parts.push(
        srOnly(labels.regular) +
        '<s class="vp-v2__price-compare' + (hooks.compare ? " " + compareClass + '"' + hooks.compare : '"') + ">" +
          compareMoney +
        "</s>"
      );
    }
    parts.push(
      (compareMoney ? srOnly(labels.sale) : "") +
      '<span class="vp-v2__price-current' + (hooks.current ? ' igPrice"' + hooks.current : '"') + ">" +
        currentMoney +
      "</span>"
    );
    // The per-day line lives inside the price container (that container is the
    // grid's `price` area), so it has to be re-emitted here or the first variant
    // change would wipe it.
    if (perDayMoney) {
      var perDayValue = hooks.perDay
        ? '<span class="igPrice"' + hooks.perDay + ">" + perDayMoney + "</span>"
        : perDayMoney;
      parts.push(
        '<span class="vp-v2__price-per-day" ' + perDayAttr + ">" +
          perDayValue + (perDayLabel ? " " + perDayLabel : "") +
        "</span>"
      );
    }
    return parts.join(" ");
  }

  // Subscription discount % for a variant: (base - sub) / base, where base is
  // the compare-at price when on sale, else the regular price. Mirrors the
  // Liquid init_pct math in product-variant-picker-v2.liquid. Returns null
  // when there's no allocation or no positive discount.
  function discountPct(v) {
    if (!v || v.sub_price == null) return null;
    var base = v.compare_at_price != null ? v.compare_at_price : v.price;
    if (!base || base <= 0) return null;
    var pct = Math.round(((base - v.sub_price) / base) * 100);
    return pct > 0 ? pct : null;
  }

  // The struck-through figure beside the subscription price: the compare-at
  // when the variant is marked down, else the one-time price (the single jar
  // has no compare-at). The same base discountPct() uses, so the struck price
  // and the Save badge agree. Mirrors sub_compare in
  // product-variant-picker-v2.liquid. Null when it isn't above the price the
  // panel charges.
  function subCompare(v) {
    var paid = v.sub_price != null ? v.sub_price : v.price;
    if (v.compare_at_price != null) {
      return v.compare_at_price > paid ? { money: v.compare_at_money, isPrice: false } : null;
    }
    return v.price > paid ? { money: v.price_money, isPrice: true } : null;
  }

  // One-time discount %: one-time price vs compare-at (MSRP). Null when the
  // variant has no compare-at markdown (e.g. the single jar), so the One Time
  // Purchase badge hides itself for those.
  function otpPct(v) {
    if (!v || v.compare_at_price == null) return null;
    var base = v.compare_at_price;
    if (!base || base <= v.price) return null;
    var pct = Math.round(((base - v.price) / base) * 100);
    return pct > 0 ? pct : null;
  }

  // ---- Loyalty points -------------------------------------------------
  // Rivo's "Place an order" rule is a flat multiplier (points per 1 spent) with
  // no VIP tier multipliers configured, so one rate is correct for every
  // customer. Liquid server-renders the number from the theme setting; this
  // reads Rivo's live config and corrects it if the merchant changed the rate
  // in Rivo without updating the theme. Returns:
  //   a number  -> use this rate (0 means the program is off / product excluded)
  //   null      -> Rivo not readable, keep whatever Liquid rendered
  function rivoPointsRate(productId) {
    var cfg;
    try {
      cfg = window.Rivo && window.Rivo.loy_config;
    } catch (e) {
      return null;
    }
    if (!cfg) return null;
    if (cfg.points_program_enabled === false) return 0;

    // Product-level exclusions. (Collection exclusions aren't resolvable
    // client-side; those stay the merchant's responsibility to mirror in the
    // theme setting.)
    var os = cfg.order_settings || {};
    var lists = [os.points_program_excluded_product_ids, os.skip_order_earnings_on_product_ids];
    for (var l = 0; l < lists.length; l++) {
      var list = lists[l];
      if (!Array.isArray(list) || !productId) continue;
      for (var k = 0; k < list.length; k++) {
        if (String(list[k]) === String(productId)) return 0;
      }
    }

    var ways = cfg.ways_to_earn;
    if (!Array.isArray(ways)) return null;
    for (var i = 0; i < ways.length; i++) {
      var r = ways[i];
      if (!r || r.trigger !== "order_placed") continue;
      if (r.status && r.status !== "active") return 0;
      var amount = Number(r.points_amount);
      var base = Number(r.currency_base_amount == null ? 1 : r.currency_base_amount);
      if (!isFinite(amount) || amount < 0 || !isFinite(base) || base <= 0) return null;
      return amount / base;
    }
    return null;
  }

  function init(root) {
    if (!root || root.__vpV2Inited) return;
    root.__vpV2Inited = true;

    var variantsScript = root.querySelector("[data-vp-v2-variants]");
    var variants = {};
    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error("[vp-v2] invalid variants JSON", e);
      }
    }

    var modeRadios = root.querySelectorAll('input[name^="vp-v2-mode-"]');
    var subRadio = root.querySelector('input[name^="vp-v2-mode-"][value="subscription"]');
    var otpRadio = root.querySelector('input[name^="vp-v2-mode-"][value="onetime"]');
    var subPanel = subRadio ? subRadio.closest(".vp-v2__mode") : null;
    var otpPanel = otpRadio ? otpRadio.closest(".vp-v2__mode") : null;
    // Scoped to this picker's own root. It used to be a document-wide lookup
    // for the shared "#nx-sub-toggle-input" id, which resolved to whichever
    // toggle came first in the DOM — this one — and so also stole the stock
    // picker's toggle out from under it. Both now carry [data-nx-sub-toggle]
    // and global.js resolves the displayed one.
    var compatToggle = root.querySelector(".vp-v2__sub-toggle-compat");
    var subPriceEl = root.querySelector("[data-vp-v2-price-sub]");
    var otpPriceEl = root.querySelector("[data-vp-v2-price-otp]");
    var deliveryNameEl = root.querySelector("[data-vp-v2-delivery-name]");
    var pillInputs = root.querySelectorAll(".vp-v2__pill-input");
    var discountPerkEl = root.querySelector(".vp-v2__perk--discount");
    var discountPctEl = root.querySelector("[data-vp-v2-discount-pct]");
    var freeShipPerkEl = root.querySelector(".vp-v2__perk--free-ship");
    var freeShipOtpEl = root.querySelector(".vp-v2__perks--otp");
    // The free-shipping ROW inside that list. It gained a sibling (the static
    // bundle-mode free-gift row), so the row hides on its own and the list only
    // collapses once nothing inside it is left visible.
    var freeShipOtpRowEl = root.querySelector(".vp-v2__perk--free-ship-otp");
    var subBadgeEl = root.querySelector("[data-vp-v2-sub-badge]");
    var subBadgePctEl = root.querySelector("[data-vp-v2-sub-badge-pct]");
    var otpBadgeEl = root.querySelector("[data-vp-v2-otp-badge]");
    var otpBadgePctEl = root.querySelector("[data-vp-v2-otp-badge-pct]");
    var pointsPerkEl = root.querySelector(".vp-v2__perk--points");
    var pointsSubEl = root.querySelector("[data-vp-v2-points-sub]");
    // The signup bonus is a flat one-off: one bonus per subscription started, so it
    // does not scale with quantity or variant and is never recomputed here. It is
    // only shown/hidden, alongside the recurring row it belongs with.
    var pointsBonusPerkEl = root.querySelector(".vp-v2__perk--points-bonus");
    // Liquid "Save up to" figures that never change with the selection, redone
    // only for a shopper in a price test (renderTestSavings below).
    var pillSavingsEls = root.querySelectorAll(".vp-v2__pill-savings[data-vp-v2-savings-variant]");
    var chipPctEl = root.querySelector("[data-vp-v2-chip-pct]");

    // Absent attribute = the perk didn't render (disabled, rate 0, or a
    // currency the rate isn't denominated in). Stays 0 so nothing shows.
    var pointsRate = parseFloat(root.getAttribute("data-vp-v2-points-rate"));
    if (!isFinite(pointsRate) || pointsRate < 0) pointsRate = 0;
    // Subscriptions earn at a multiple of the base rate. This is a theme setting,
    // NOT something Rivo exposes in loy_config, so the reconcile below deliberately
    // only corrects the base rate and leaves this multiplier alone.
    var subMultiplier = parseFloat(root.getAttribute("data-vp-v2-points-sub-multiplier"));
    if (!isFinite(subMultiplier) || subMultiplier < 1) subMultiplier = 1;
    // Flat signup bonus, folded into the subscribe row's total. One per
    // subscription started, so it is added once and never multiplied by
    // quantity the way the per-order points are.
    var subBonus = parseFloat(root.getAttribute("data-vp-v2-points-sub-bonus"));
    if (!isFinite(subBonus) || subBonus < 0) subBonus = 0;
    var productId = root.getAttribute("data-vp-v2-product-id");
    // Always rendered, unlike data-vp-v2-product-id (points only): the
    // Intelligems price hooks need it.
    var hookProductId = root.getAttribute("data-vp-v2-pid") || productId;
    // Free shipping is earned on the ORDER, not on one unit, so the threshold
    // has to be re-tested as quantity changes -- 2 x a $59.99 jar clears an $80
    // threshold even though the unit price does not. The server-rendered
    // free_ship flags stay correct for the qty-1 first paint.
    var freeShipThreshold = parseFloat(root.getAttribute("data-vp-v2-free-ship-threshold"));
    if (!isFinite(freeShipThreshold) || freeShipThreshold < 0) freeShipThreshold = 0;
    var subsAlwaysShipFree = root.getAttribute("data-vp-v2-subs-ship-free") === "true";

    // Deliberately ignores what else is already in the cart: this can only
    // under-promise, never claim free shipping the customer has not earned.
    function qualifiesFreeShip(cents, alwaysFree) {
      if (alwaysFree) return true;
      if (!freeShipThreshold || cents == null) return false;
      return cents * currentQty() >= freeShipThreshold;
    }
    // Locale-owned, read off the root rather than hardcoded, so Translate & Adapt
    // and the es locale keep working after a JS-driven price rebuild.
    var perDayLabel = root.getAttribute("data-vp-v2-per-day-label") || "";
    var priceLabels = {
      regular: root.getAttribute("data-vp-v2-regular-label") || "",
      sale: root.getAttribute("data-vp-v2-sale-label") || ""
    };

    // The PDP quantity stepper lives outside this block, in the same section. Look it
    // up on every read rather than caching the node: a product-family swap replaces the
    // whole main section, and Dawn rewrites the quantity form on every variant change.
    var sectionEl = root.closest(".shopify-section") || document;
    var currentVariantId = null;

    function currentQty() {
      var el = sectionEl.querySelector('input[name="quantity"]');
      if (!el) return 1;
      var q = parseInt(el.value, 10);
      return isFinite(q) && q > 0 ? q : 1;
    }

    // Mirrors Rivo: whole points, rounded, on the discounted item total.
    function pointsFor(cents, multiplier) {
      if (!pointsRate || cents == null) return 0;
      return Math.round((cents / 100) * pointsRate * (multiplier || 1) * currentQty());
    }

    function renderPointsRow(perkEl, numEl, cents, multiplier, bonus) {
      if (!perkEl || !numEl) return false;
      var pts = pointsFor(cents, multiplier);
      // Only add the bonus when there are points to add it to -- a zero rate
      // means the programme is off, and a lone bonus would be a false claim.
      if (pts > 0 && bonus) pts += bonus;
      if (pts > 0) {
        numEl.textContent = pts;
        perkEl.style.display = "";
        return true;
      }
      perkEl.style.display = "none";
      return false;
    }

    function getCurrentVariantId() {
      var checked = root.querySelector(".vp-v2__pill-input:checked");
      if (checked && checked.dataset.variantId) return checked.dataset.variantId;
      var hidden = document.querySelector('product-form input[name="id"]');
      return hidden ? hidden.value : null;
    }

    // This variant's prices for THIS shopper. In an Intelligems price test that
    // means the test price, with every derived figure worked out the way Shopify
    // will charge it. Otherwise it is the Liquid JSON as-is, money strings
    // included, so anyone outside a test sees exactly what the server rendered.
    function priced(variantId) {
      var v = variants[variantId];
      if (!v) return null;
      var api = igApi();
      var testPrice = api ? api.priceCents(variantId) : null;
      if (testPrice == null) return v;

      var out = {};
      for (var key in v) {
        if (Object.prototype.hasOwnProperty.call(v, key)) out[key] = v[key];
      }
      var compare = api.compareCents(variantId);
      if (compare == null) compare = v.compare_at_price;
      out.price = testPrice;
      out.compare_at_price = compare != null && compare > testPrice ? compare : null;
      out.sub_pct = api.planPercent(variantId, v.sub_pct);
      if (v.sub_price != null) {
        if (out.sub_pct != null) {
          out.sub_price = api.subscriptionCents(testPrice, out.sub_pct);
        } else if (v.price > 0) {
          out.sub_price = Math.round((v.sub_price * testPrice) / v.price);
        }
      }
      out.price_money = api.formatMoney(out.price);
      out.compare_at_money = out.compare_at_price != null ? api.formatMoney(out.compare_at_price) : null;
      out.sub_price_money = out.sub_price != null ? api.formatMoney(out.sub_price) : null;
      if (v.per_day_divisor > 0) {
        out.per_day_otp_money = api.formatMoney(Math.round(out.price / v.per_day_divisor));
        out.per_day_sub_money = api.formatMoney(
          Math.round((out.sub_price != null ? out.sub_price : out.price) / v.per_day_divisor)
        );
      }
      out.isTestPrice = true;
      return out;
    }

    // Intelligems attribute strings for one panel. The subscription panel shows
    // the one-time price for a variant with no plan, and gets no subscription
    // hooks when the plan isn't a single percentage, which a multiplier can't
    // express.
    function hooksFor(v, variantId, panel) {
      var divisor = v.per_day_divisor > 0 ? v.per_day_divisor : null;
      var multiplier = 1;
      if (panel === "sub" && v.sub_price != null) {
        var pct = v.sub_pct != null ? parseFloat(v.sub_pct) : NaN;
        if (!isFinite(pct)) return { compare: igAttrs(hookProductId, variantId) };
        multiplier = (100 - pct) / 100;
      }
      return {
        compare: igAttrs(hookProductId, variantId),
        current: igAttrs(hookProductId, variantId, multiplier),
        perDay: divisor ? igAttrs(hookProductId, variantId, multiplier / divisor) : ""
      };
    }

    function renderForVariant(variantId) {
      if (!variantId) return;
      var v = priced(variantId);
      if (!v) return;
      currentVariantId = variantId;
      if (otpPriceEl) {
        otpPriceEl.innerHTML = buildPriceHTML(
          v.compare_at_money, v.price_money, v.per_day_otp_money, "data-vp-v2-per-day-otp", perDayLabel,
          hooksFor(v, variantId, "otp"), priceLabels
        );
      }
      if (subPriceEl) {
        var subMoney = v.sub_price_money != null ? v.sub_price_money : v.price_money;
        var subHooks = hooksFor(v, variantId, "sub");
        var subStruck = subCompare(v);
        if (subStruck && subStruck.isPrice) subHooks.compareClass = "igPrice";
        subPriceEl.innerHTML = buildPriceHTML(
          subStruck ? subStruck.money : null, subMoney, v.per_day_sub_money, "data-vp-v2-per-day-sub", perDayLabel,
          subHooks, priceLabels
        );
      }
      if (deliveryNameEl) deliveryNameEl.textContent = formatPlanName(v.plan_name);
      // Subscription discount % (sub vs compare-at) — shared by the perk line
      // and the Subscribe & Save panel badge.
      var subPct = discountPct(v);
      if (discountPerkEl && discountPctEl) {
        if (subPct != null) {
          discountPctEl.textContent = subPct;
          discountPerkEl.style.display = "";
        } else {
          discountPerkEl.style.display = "none";
        }
      }
      if (subBadgeEl && subBadgePctEl) {
        if (subPct != null) {
          subBadgePctEl.textContent = subPct;
          subBadgeEl.style.display = "";
        } else {
          subBadgeEl.style.display = "none";
        }
      }
      // One-time discount % (price vs compare-at) — One Time Purchase badge.
      if (otpBadgeEl && otpBadgePctEl) {
        var otpVal = otpPct(v);
        if (otpVal != null) {
          otpBadgePctEl.textContent = otpVal;
          otpBadgeEl.style.display = "";
        } else {
          otpBadgeEl.style.display = "none";
        }
      }
      // Free-shipping perk — re-tested against the order total, not the unit price.
      if (freeShipPerkEl) {
        var subCents = v.sub_price != null ? v.sub_price : v.price;
        freeShipPerkEl.style.display = qualifiesFreeShip(subCents, subsAlwaysShipFree) ? "" : "none";
      }
      // Loyalty points. Subscribe keys off the subscription price, One Time
      // Purchase off the one-time price — each is what the customer actually
      // pays, which is what Rivo awards on.
      renderPointsRow(pointsPerkEl, pointsSubEl, v.sub_price != null ? v.sub_price : v.price, subMultiplier, subBonus);
      // If the reconcile turned the programme off (or this product earns nothing),
      // the signup bonus is not payable either -- hide it rather than leave a claim
      // standing next to a row that just disappeared.
      if (pointsBonusPerkEl) {
        pointsBonusPerkEl.style.display = pointsRate > 0 ? "" : "none";
      }
      // One Time Purchase list. Hide the free-shipping ROW on its own, then
      // collapse the whole list only when every row in it is hidden — the
      // free-gift row is static and bundle-mode-only, so on a bundle the list
      // must survive a variant that misses the free-shipping threshold.
      if (freeShipOtpRowEl) {
        freeShipOtpRowEl.style.display = qualifiesFreeShip(v.price, false) ? "" : "none";
      }
      if (freeShipOtpEl) {
        var otpRows = freeShipOtpEl.querySelectorAll(".vp-v2__perk");
        var otpVisible = false;
        for (var i = 0; i < otpRows.length; i++) {
          if (otpRows[i].style.display !== "none") {
            otpVisible = true;
            break;
          }
        }
        freeShipOtpEl.style.display = otpVisible ? "" : "none";
      }
    }

    // Translate & Adapt rewrites the bracketed marker along with the sentence
    // ("[importe]"), so match any bracketed token, as global.js does.
    var AMOUNT_TOKEN = /\[[^\]]+\]/;

    // The pack pills' "Save up to $X" (and its hidden percent twin, TASKS #137)
    // and the chip's "Save up to X%" are worked out in Liquid against catalog
    // prices. Redo them for a shopper in a price test. Liquid's text is kept the
    // first time and put back once the shopper has no test price, e.g. after a
    // preview switches group without a reload.
    function keepLiquidText(el) {
      if (el.__vpV2LiquidText != null) return;
      el.__vpV2LiquidText = el.textContent;
      el.__vpV2LiquidDisplay = el.style.display;
    }

    function restoreLiquidText(el) {
      if (el.textContent !== el.__vpV2LiquidText) el.textContent = el.__vpV2LiquidText;
      el.style.display = el.__vpV2LiquidDisplay;
    }

    function renderTestSavings() {
      var api = igApi();
      if (!api) return;
      Array.prototype.forEach.call(pillSavingsEls, function (el) {
        keepLiquidText(el);
        var v = priced(el.getAttribute("data-vp-v2-savings-variant"));
        if (!v || !v.isTestPrice) {
          restoreLiquidText(el);
          return;
        }
        var base = v.compare_at_price != null ? v.compare_at_price : v.price;
        var paid = v.sub_price != null ? v.sub_price : v.price;
        var template = el.getAttribute("data-vp-v2-savings-template") || "";
        var isPercent = el.getAttribute("data-vp-v2-savings-format") === "percent";
        var shown = base - paid;
        // Same rounding as discountPct(), which the Subscribe badge uses.
        if (isPercent) shown = base > 0 ? Math.round((shown / base) * 100) : 0;
        if (shown > 0 && AMOUNT_TOKEN.test(template)) {
          el.textContent = template.replace(AMOUNT_TOKEN, isPercent ? String(shown) : api.formatMoney(shown, true));
          el.style.display = "";
        } else {
          el.style.display = "none";
        }
      });
      if (chipPctEl) {
        keepLiquidText(chipPctEl);
        var best = 0;
        var tested = false;
        for (var id in variants) {
          if (!Object.prototype.hasOwnProperty.call(variants, id)) continue;
          var pv = priced(id);
          if (pv && pv.isTestPrice) tested = true;
          var pct = discountPct(pv);
          if (pct != null && pct > best) best = pct;
        }
        if (tested && best > 0) {
          if (chipPctEl.textContent !== String(best)) chipPctEl.textContent = best;
        } else {
          restoreLiquidText(chipPctEl);
        }
      }
    }

    function syncActivePanelClass() {
      if (subPanel) subPanel.classList.toggle("vp-v2__mode--active", !!(subRadio && subRadio.checked));
      if (otpPanel) otpPanel.classList.toggle("vp-v2__mode--active", !!(otpRadio && otpRadio.checked));
    }

    function syncCompatToggle() {
      if (!compatToggle) return;
      var wantSub = !!(subRadio && subRadio.checked);
      if (compatToggle.checked === wantSub) return;
      compatToggle.checked = wantSub;
      // Bubble the change so global.js's wireCheckbox handler runs.
      compatToggle.dispatchEvent(new Event("change", { bubbles: true }));
    }

    function onModeChange() {
      syncActivePanelClass();
      syncCompatToggle();
    }

    Array.prototype.forEach.call(modeRadios, function (r) {
      r.addEventListener("change", onModeChange);
    });

    Array.prototype.forEach.call(pillInputs, function (input) {
      input.addEventListener("change", function () {
        var id = input.dataset.variantId;
        if (id) renderForVariant(id);
      });
    });

    // "with this order" has to mean the whole order, so the points track the quantity
    // stepper. Delegated to the section (not bound to the input) so it keeps working
    // after Dawn rewrites the quantity form. Both events: `input` for typing, `change`
    // for the +/- buttons and the browser's own commit.
    {
      ["input", "change"].forEach(function (evt) {
        sectionEl.addEventListener(evt, function (e) {
          var t = e.target;
          if (!t || t.name !== "quantity") return;
          if (currentVariantId) renderForVariant(currentVariantId);
        });
      });
    }

    // Initial render — variants JSON has up-to-date prices for whatever
    // variant the server selected (which is correct after any
    // ?variant=<id> URL from the family selector or direct PDP load).
    var initialId = getCurrentVariantId();
    if (initialId) renderForVariant(initialId);
    syncActivePanelClass();
    syncCompatToggle();
    renderTestSavings();

    // intelligems-bridge.js fires jiyu:intelligems-ready once test prices are
    // readable, which usually lands after this init: the Intelligems bundle is
    // async and doesn't hold first paint. The listener at the bottom of this file
    // calls this on every live picker.
    root.__vpV2Reprice = function () {
      if (currentVariantId) renderForVariant(currentVariantId);
      renderTestSavings();
    };

    // Dawn resets the quantity to 1 as part of a variant change, and does it WITHOUT
    // firing an input/change event - so the points would keep showing the pre-change
    // quantity's total, overstating what the customer earns. Re-render once its update
    // has landed. Guarded: `subscribe` is undefined in contexts where pubsub.js didn't
    // load, and the picker must still work there.
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS === "object") {
      if (typeof root.__vpV2Unsub === "function") root.__vpV2Unsub();
      root.__vpV2Unsub = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var id = event && event.data && event.data.variant && event.data.variant.id;
        renderForVariant(id ? String(id) : currentVariantId);
      });
    }

    // Reconcile the earn rate against Rivo's live config. The app embed loads
    // async, so poll briefly — setTimeout, not rAF, so this still runs when the
    // tab is backgrounded. Stops at the first readable answer; if Rivo never
    // becomes readable the Liquid-rendered number stands.
    if (pointsRate) {
      var attempts = 0;
      (function checkRivo() {
        var rate = rivoPointsRate(productId);
        if (rate == null) {
          if (attempts++ < 15) setTimeout(checkRivo, 250);
          return;
        }
        if (rate === pointsRate) return;
        pointsRate = rate;
        if (currentVariantId) renderForVariant(currentVariantId);
      })();
    }

    // Pre-select the 2nd pill (the recommended option) on a fresh load when the
    // URL doesn't request a specific variant. Dispatching change drives Dawn's
    // variant-selects -> product-info so the form id, price and media all sync,
    // and the V2 panels update via the pill's own change handler. After the
    // first selection the URL carries ?variant=, so this no-ops on re-init; it
    // also skips when the 2nd pill is already selected or unavailable.
    if (!/[?&]variant=/.test(window.location.search) && pillInputs.length >= 2) {
      var defaultPill = pillInputs[1];
      if (defaultPill && !defaultPill.checked && !defaultPill.disabled) {
        defaultPill.checked = true;
        defaultPill.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  function initAll() {
    var roots = document.querySelectorAll("[data-vp-v2-section]");
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Re-init when the customizer reloads a section (preview only).
  document.addEventListener("shopify:section:load", function (e) {
    var target = e.target;
    if (!target) return;
    var root = null;
    if (target.matches && target.matches("[data-vp-v2-section]")) {
      root = target;
    } else if (target.querySelector) {
      root = target.querySelector("[data-vp-v2-section]");
    }
    if (root) {
      root.__vpV2Inited = false;
      init(root);
    }
  });

  // Intelligems test prices became readable (TASKS #133). One document-level
  // listener that re-prices whichever pickers are live now, so pickers replaced
  // by a section swap don't each leave a listener behind.
  document.addEventListener("jiyu:intelligems-ready", function () {
    var roots = document.querySelectorAll("[data-vp-v2-section]");
    Array.prototype.forEach.call(roots, function (root) {
      if (typeof root.__vpV2Reprice === "function") root.__vpV2Reprice();
    });
  });
})();
